// Lightweight web-vitals + tab-switch reporter. Batches into `perf_events`
// every few seconds so the platform Performance panel has data to chart.
// No-ops for unauthenticated visitors (public marketing pages).
import { onCLS, onFCP, onINP, onLCP, onTTFB, type Metric } from 'web-vitals';
import { supabase } from '@/integrations/supabase/client';

type Pending = {
  user_id: string | null;
  agency_id: string | null;
  route: string;
  metric: string;
  value: number;
  rating: string | null;
  meta: Record<string, unknown> | null;
};

const buffer: Pending[] = [];
let flushTimer: number | null = null;
let installed = false;

function scheduleFlush() {
  if (flushTimer !== null) return;
  flushTimer = window.setTimeout(flush, 4000);
}

async function flush() {
  flushTimer = null;
  if (buffer.length === 0) return;
  const batch = buffer.splice(0, buffer.length);
  try {
    await supabase.from('perf_events' as any).insert(batch);
  } catch {
    // best-effort telemetry — never throw into user code
  }
}

async function currentUserId(): Promise<string | null> {
  try {
    const { data } = await supabase.auth.getSession();
    return data.session?.user?.id ?? null;
  } catch {
    return null;
  }
}

async function record(metric: string, value: number, rating?: string | null, meta?: Record<string, unknown>) {
  const user_id = await currentUserId();
  if (!user_id) return; // skip anonymous telemetry
  buffer.push({
    user_id,
    agency_id: null,
    route: window.location.pathname,
    metric,
    value: Math.round(value * 100) / 100,
    rating: rating ?? null,
    meta: meta ?? null,
  });
  scheduleFlush();
}

function handleVital(m: Metric) {
  void record(m.name, m.value, m.rating, { id: m.id, navigationType: m.navigationType });
}

export function installPerfReporter() {
  if (installed || typeof window === 'undefined') return;
  installed = true;

  onCLS(handleVital);
  onFCP(handleVital);
  onINP(handleVital);
  onLCP(handleVital);
  onTTFB(handleVital);

  // Tab-switch timing: route-change duration measured by the router wrapper.
  window.addEventListener('beforeunload', () => {
    if (buffer.length === 0) return;
    try {
      const url = `${(import.meta as any).env?.VITE_SUPABASE_URL ?? ''}/rest/v1/perf_events`;
      navigator.sendBeacon?.(url, new Blob([JSON.stringify(buffer)], { type: 'application/json' }));
    } catch {
      // ignore
    }
  });
}

export function recordTabSwitch(fromRoute: string, toRoute: string, durationMs: number) {
  void record('TAB_SWITCH', durationMs, durationMs < 200 ? 'good' : durationMs < 600 ? 'needs-improvement' : 'poor', {
    from: fromRoute,
    to: toRoute,
  });
}
